import { useState } from 'react';
import { Turnstile } from '@marsidev/react-turnstile';
import './Reach.css';
import logo from '../assets/dangol-logo.png';

const Reach = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    businessName: '',
    phoneNumber: '',
    email: '',
    website: '' // honeypot
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [submitStatus, setSubmitStatus] = useState('');
  const [pageToken, setPageToken] = useState('');
  const [submitToken, setSubmitToken] = useState('');

  const formatPhoneNumber = (value) => {
    const phoneNumber = value.replace(/\D/g, '');
    if (!phoneNumber) return '';
    if (phoneNumber.length <= 3) return `(${phoneNumber}`;
    if (phoneNumber.length <= 6) return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'phoneNumber') {
      setFormData(prev => ({ ...prev, [name]: formatPhoneNumber(value) }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const required = ['firstName', 'lastName', 'businessName', 'phoneNumber', 'email'];
    for (let f of required) {
      if (!formData[f].trim()) {
        setSubmitMessage(`Please fill in the ${f.replace(/([A-Z])/g, ' $1').toLowerCase()} field.`);
        setSubmitStatus('error');
        return;
      }
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setSubmitMessage('Please enter a valid email address.');
      setSubmitStatus('error');
      return;
    }

    if (!submitToken) {
      setSubmitMessage('Please complete the security verification.');
      setSubmitStatus('error');
      return;
    }

    setIsSubmitting(true);
    setSubmitMessage('');
    setSubmitStatus('');

    try {
      const response = await fetch('/api/send-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName: formData.firstName,
          lastName: formData.lastName,
          businessName: formData.businessName,
          phoneNumber: formData.phoneNumber,
          email: formData.email,
          website: formData.website,
          pageToken,
          submitToken,
        }),
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to send');

      setSubmitMessage("Thanks! We'll be in touch soon to set up your founding partner account.");
      setSubmitStatus('success');
      setFormData({ firstName: '', lastName: '', businessName: '', phoneNumber: '', email: '', website: '' });
      setSubmitToken('');
    } catch (error) {
      setSubmitMessage('Sorry, there was an error submitting your form. Please try again.');
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="reach-page">
      <div className="reach-container">

        <div className="reach-logo">
          <img src={logo} alt="Dangol" />
        </div>

        <h1 className="reach-headline">
          There's a better way to reach your regulars.
        </h1>

        <p className="reach-subhead">
          Social media algorithms only show your posts to a fraction of your followers.
          Dangol sends every message straight to their lock screen — no algorithm, no guessing.
        </p>

        <ul className="reach-bullets">
          <li>
            <strong>Direct push notifications.</strong> Your message hits every regular's phone, every time.
          </li>
          <li>
            <strong>Customer insights.</strong> See who's coming back, who's slipping away, and what's working.
          </li>
          <li>
            <strong>Free for founding partners.</strong> Full access, zero cost, just your honest feedback.
          </li>
        </ul>

        <div className="reach-form-wrapper">
          {submitMessage && (
            <div className={`reach-overlay ${submitStatus}`}>
              <div className="reach-overlay-content">
                <div className={`reach-overlay-message ${submitStatus}`}>
                  {submitMessage}
                </div>
                <button
                  className="reach-overlay-close"
                  onClick={() => { setSubmitMessage(''); setSubmitStatus(''); }}
                >
                  Close
                </button>
              </div>
            </div>
          )}

          <form className="reach-form" onSubmit={handleSubmit}>
            <input
              type="text"
              name="businessName"
              placeholder="Shop name"
              value={formData.businessName}
              onChange={handleInputChange}
              required
            />
            <div className="reach-form-row">
              <input
                type="text"
                name="firstName"
                placeholder="First name"
                value={formData.firstName}
                onChange={handleInputChange}
                required
              />
              <input
                type="text"
                name="lastName"
                placeholder="Last name"
                value={formData.lastName}
                onChange={handleInputChange}
                required
              />
            </div>
            <input
              type="email"
              name="email"
              placeholder="Email"
              value={formData.email}
              onChange={handleInputChange}
              required
            />
            <input
              type="tel"
              name="phoneNumber"
              placeholder="Phone (123) 456-7890"
              value={formData.phoneNumber}
              onChange={handleInputChange}
              maxLength="14"
              pattern="^\(\d{3}\) \d{3}-\d{4}$"
              title="Please enter phone in format: (123) 456-7890"
              required
            />

            {/* Honeypot */}
            <div style={{ position: 'absolute', left: '-9999px' }} aria-hidden="true">
              <input
                type="text"
                name="website"
                value={formData.website}
                onChange={handleInputChange}
                tabIndex="-1"
                autoComplete="off"
              />
            </div>

            <div className="reach-turnstile">
              <Turnstile
                siteKey="0x4AAAAAAB_jqx5UPufLw8-x"
                onSuccess={(t) => setSubmitToken(t)}
                onError={() => setSubmitToken('')}
                onExpire={() => setSubmitToken('')}
                options={{ theme: 'light', size: 'normal' }}
              />
            </div>

            <button type="submit" className="reach-submit" disabled={isSubmitting}>
              {isSubmitting ? 'Submitting...' : 'Get Started — Free'}
            </button>
          </form>

          <p className="reach-disclaimer">
            We're onboarding a small group of Chicago shops as founding partners.
            Full access, completely free. We just want your honest feedback.
          </p>
        </div>

        <div style={{ position: 'absolute', left: '-9999px', visibility: 'hidden' }} aria-hidden="true">
          <Turnstile
            siteKey="0x4AAAAAAB_jqx5UPufLw8-x"
            onSuccess={(t) => setPageToken(t)}
            onError={() => setPageToken('')}
            onExpire={() => setPageToken('')}
            options={{ theme: 'light', size: 'invisible' }}
          />
        </div>

      </div>
    </div>
  );
};

export default Reach;
